import { PercentPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { getCSSVariableValue } from '../../../_metronic/kt/_utils';
import { CurrencyPipe } from '../../currency/currency.pipe';
import { DatePipe } from '../../date/date.pipe';
import { IDashboardChartPallete } from '../dashboard-card/dashboard-card.service';

const gray200Color = getCSSVariableValue('--bs-gray-200');
const gray400Color = getCSSVariableValue('--bs-gray-400');

@Component({
  selector: 'app-dashboard-chart',
  templateUrl: './dashboard-chart.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardChartComponent {
  @Input()
  public bigChartStats: any[];

  @Input()
  public isFakeData: boolean = false;

  @Input()
  public selectedChartsPalletes: Map<number, IDashboardChartPallete> | null = new Map();

  constructor(
    private readonly datePipe: DatePipe,
    private readonly percentPipe: PercentPipe,
    private readonly currencyPipe: CurrencyPipe,
    private readonly translateService: TranslateService,
  ) {}

  public get chartOptions(): any {
    const selectedColors = [...(this.selectedChartsPalletes?.values() || [])].reduce((acc: any, item) => {
      acc.asVars.push(`var(${item.chartBgColor})`);
      acc.asCssVars.push(getCSSVariableValue(item.chartBgColor || ''));
      return acc;
    }, {
      asVars: [],
      asCssVars: []
    });

    const colors = this.isFakeData ? [gray400Color] : selectedColors.asVars;

    const zoomIconTitle = this.translateService.instant('DASHBOARD.SUMMARY.ZOOM_TITLE');

    return {
      chart: {
        fontFamily: 'inherit',
        type: 'area',
        height: 350,
        animations: {
          enabled: false
        },
        toolbar: {
          show: true,
          tools: {
            download: false,
            selection: false,
            /**
             * Copied SVG from ApexChart
             */
            zoom: `
              <div title="${zoomIconTitle}">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  height="24"
                  viewBox="0 0 24 24"
                  width="24"
                  style="fill: var(--bs-primary) !important;"
                >
                  <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
                  <path d="M0 0h24v24H0V0z" fill="none"></path>
                  <path d="M12 10h-2v2H9v-2H7V9h2V7h1v2h2v1z"></path>
                </svg>
              </div>
            `,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: true,
          },
        },
        zoom: {
          enabled: false,
          allowMouseWheelZoom: false,
        },
      },
      plotOptions: {},
      legend: {
        position: 'top',
        horizontalAlign: 'left',
        onItemClick: {
          toggleDataSeries: !this.isFakeData,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        show: true,
        width: 3,
        colors,
      },
      xaxis: {
        type: 'date',
        tooltip: {
          enabled: false
        },
        labels: {
          formatter: (value: any) => this.datePipe.transform(value),
        }
      },
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0,
          },
        },
        hover: {
          filter: {
            type: 'none',
            value: 0,
          },
        },
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
            value: 0,
          },
        },
      },
      tooltip: {
        style: {
          fontSize: '12px',
        },
        y: {
          formatter: (val: number, data: any) => {
            const { seriesIndex, w: { config: { series } } } = data;
            const currentSeriesConfig = series[seriesIndex];

            if (currentSeriesConfig?.isPercentage) {
              return this.percentPipe.transform(val/100, '1.0-2');
            }

            if (currentSeriesConfig?.isCurrency) {
              return this.currencyPipe.transform(val);
            }

            return val;
          },
        },
      },
      colors: selectedColors.asCssVars,
      grid: {
        borderColor: gray200Color,
        strokeDashArray: 4,
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      markers: {
        strokeColors: colors,
        strokeWidth: 3,
      },
    };
  }
}
